<script setup lang="ts">
import {
  X,
  SearchMd,
  Plus,
  ArrowNarrowRight,
  RefreshCcw01,
} from "utruckin-icons";
import CommonSearch from "@/components/common/CommonSearch.vue";
import CommonTable from "@/components/common/CommonTable.vue";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { DateTime } from "luxon";
import { useCompanyStore } from "@/features/company/company.store";
import { ICompany } from "@/features/company/company.types";
import CommonButton from "@/components/common/CommonButton.vue";
import { useAppStore } from "@/store/app.store";
import CustomTab from "@/components/custom/CustomTab.vue";
import CommonModal from "@/components/common/CommonModal.vue";
import CommonInput from "@/components/common/CommonInput.vue";
import { useNotification } from "@/utils/notification";
import { useDriverStatusStore } from "@/features/driver-status/driver-status.store";
import {
  IDriverStatus,
  INewDriverStatus,
  IWatchStatus,
} from "@/features/driver-status/driver-status.types";
import CustomStatusBadge from "@/components/custom/CustomStatusBadge.vue";
import CommonDropdown from "@/components/common/CommonDropdown.vue";
import CustomTableDropdown from "@/components/custom/CustomTableDropdown.vue";
import { useDriverStore } from "@/features/drivers/drivers.store";
import { IDriver } from "@/features/drivers/drivers.types";
import { ITeam } from "@/features/teams/team.types";
import { useTeamStore } from "@/features/teams/team.store";
import { useUserStore } from "@/features/users/user.store";

const companyStore = useCompanyStore();
const appStore = useAppStore();

const driverStatusStore = useDriverStatusStore();
const driverStore = useDriverStore();
const teamStore = useTeamStore();
const usersStore = useUserStore();

const { pushNotification } = useNotification();

const loading = ref(false);
const search = ref<string>("");
const selectedItems = ref([]);
const companyTable = ref();
const activeTab = ref<"" | IWatchStatus>("");
const watches = ref<IDriverStatus[]>([]);
const tabs = [
  { label: "All", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Done", value: "done" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Expired", value: "expired" },
  { label: "Failed", value: "failed" },
];

const team = ref<string>("");
const teams = ref<ITeam[]>([]);
const teamLoading = ref(false);

const companies = ref<ICompany[]>([]);
const companyLoading = ref(false);

const companyId = ref<string>("");

const drivers = ref<IDriver[]>([]);
const driverLoading = ref(false);

const isOpenModal = ref<boolean>(false);
const modalItem = ref<INewDriverStatus>({
  driver_id: "",
  target_status: "off",
  note: "",
  expires_in_hours: "",
});

let searchTimer: ReturnType<typeof setTimeout> | null = null;
let searchCompanyTimer: ReturnType<typeof setTimeout> | null = null;
let searchDriverTimer: ReturnType<typeof setTimeout> | null = null;

const targetStatuses = [
  { value: "off", name: "OFF" },
  { value: "sleep", name: "SB" },
  { value: "driving", name: "DR" },
  { value: "on", name: "ON" },
  { value: "yard", name: "YM" },
  { value: "personal", name: "PC" },
  { value: "any", name: "ANY" },
];

const companyItems = computed(() =>
  (companies.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
    app: c.provider_name,
  }))
);

const driverItems = computed(() =>
  (drivers.value || []).map((c: any) => ({
    value: c.id,
    name: c.name,
    app: c.truck_number,
  }))
);

const openModal = () => {
  modalItem.value = {
    driver_id: "",
    target_status: "off",
    note: "",
    expires_in_hours: "",
  };
  isOpenModal.value = true;
};

const closeModal = () => {
  modalItem.value = {
    driver_id: "",
    target_status: "off",
    note: "",
    expires_in_hours: "",
  };
  isOpenModal.value = false;
  companyId.value = "";
};

const saveModal = async () => {
  if (
    !modalItem.value ||
    !modalItem.value.driver_id ||
    !modalItem.value.expires_in_hours ||
    !modalItem.value.target_status
  )
    return;

  await driverStatusStore.createNewDriverStatus(
    loading,
    modalItem.value,
    (data: any) => {
      if (data) {
        pushNotification("Successfully saved", "success");
        closeModal();
        window.location.reload();
      }
    }
  );
};

function datepickerToUtcDateOnly(d: any) {
  return d
    ? DateTime.fromISO(d, {
        zone: "utc",
      })
        .setZone("America/New_York")
        .toFormat("MMM d, HH:mm")
    : "-";
}

const getList = async () => {
  await driverStatusStore.getDriverStatusList(loading, (res: any) => {
    if (res) {
      watches.value = res.items;
    }
  });
};

const searchCompany = (val: string) => {
  if (searchCompanyTimer) clearTimeout(searchCompanyTimer);
  searchCompanyTimer = setTimeout(async () => {
    companyStore.commonSimpleFilter.search = val;
    companies.value = (
      await companyStore.getSimpleCompanies(companyLoading)
    ).items;
  }, 400);
};

const searchDriver = (val: string) => {
  if (searchDriverTimer) clearTimeout(searchDriverTimer);
  searchDriverTimer = setTimeout(async () => {
    driverStore.commonSimpleFilter.search = val;
    const [fetchedDrivers] = await Promise.all([
      driverStore.getSimpleDrivers(driverLoading),
    ]);

    drivers.value = fetchedDrivers.items;
  }, 400);
};

const checkWatch = async (item: IDriverStatus) => {
  if (!item.id) return;

  await driverStatusStore.checkWatch(loading, item.id, async (data: any) => {
    if (data) {
      pushNotification("Successfully checked", "success");
      await getList();
    }
  });
};

const cancelWatch = async (item: IDriverStatus) => {
  if (!item.id) return;
  await driverStatusStore.cancelWatch(loading, item.id, async (data: any) => {
    if (data) {
      pushNotification("Successfully canceled", "success");
      await getList();
    }
  });
};

onMounted(async () => {
  if (driverStatusStore.commonFilter.team_id) {
    team.value = driverStatusStore.commonFilter.team_id;
  } else {
    if (usersStore.userProfile && usersStore.userProfile.team_id) {
      team.value = usersStore.userProfile.team_id;
      driverStatusStore.commonFilter.team_id = team.value;
    }
  }

  await getList();

  const [fetchedCompanies, fetchedTeams] = await Promise.all([
    companyStore.getSimpleCompanies(companyLoading),
    teamStore.getTeams(teamLoading),
  ]);

  teams.value = fetchedTeams;
  companies.value = fetchedCompanies.items;
});

onUnmounted(() => {
  driverStatusStore.clearFilter();
});

watch(search, (val) => {
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(async () => {
    driverStatusStore.commonFilter.search = val;
    await getList();
  }, 400);
});

watch(
  () => activeTab.value,
  async (newVal) => {
    driverStatusStore.commonFilter.status = newVal;
    await getList();
  },
  {
    immediate: false,
  }
);

watch(
  () => companyId.value,
  async (val) => {
    drivers.value = [];
    modalItem.value.driver_id = "";

    if (val) {
      driverStore.commonSimpleFilter.company_id = val;
      const [fetchedDrivers] = await Promise.all([
        driverStore.getSimpleDrivers(driverLoading),
      ]);

      drivers.value = fetchedDrivers.items;
    }
  }
);

watch(
  () => team.value,
  async (val: string) => {
    if (loading.value) return;
    driverStatusStore.commonFilter.team_id = val;
    await getList();
  },
  { immediate: false }
);

watch(
  () => appStore.refreshKey,
  () => {
    getList();
  },
  {
    immediate: false,
  }
);
</script>

<template>
  <div class="v-driver-status">
    <div class="header">
      <div class="header__filter">
        <CustomTab :tabs="tabs" v-model="activeTab" />
      </div>

      <div class="header__stats">
        <CommonSearch
          v-model="search"
          :icon="SearchMd"
          placeholder="Search"
          clearable
          :disabled="selectedItems.length > 0"
        />

        <CommonDropdown
          v-model="team"
          :items="teams.map((t: any) => ({ value: t.id, name: t.name }))"
          label="Team"
          position="bottom"
          dropdownFullWidth
          clearable
        />

        <CommonButton
          type="primary"
          label="Add watch"
          prepend
          :prependIcon="Plus"
          @click="openModal"
        />
      </div>
    </div>

    <CommonTable
      ref="companyTable"
      :headers="driverStatusStore.driverStatusHeaders"
      :loading="loading"
      :items="watches"
    >
      <template #id="{ index }">
        <span>{{ index + 1 }}</span>
      </template>

      <template #expires_time="{ item }">
        {{ datepickerToUtcDateOnly(item.expires_time) }}
      </template>

      <template #eld_status="{ item }">
        <div class="eld_status">
          <CustomStatusBadge :label="item.initial_status" />
          <div class="icon">
            <ArrowNarrowRight :size="14" />
          </div>
          <CustomStatusBadge :label="item.target_status" />
        </div>
      </template>

      <template #last_seen_status="{ item }">
        <CustomStatusBadge :label="item.last_seen_status" />
      </template>

      <template #actions="{ item }">
        <div class="action" v-if="item.status == 'pending'">
          <div
            class="action__item"
            v-tooltip="{
              content: 'Check',
              html: true,
            }"
            @click="checkWatch(item)"
          >
            <RefreshCcw01 :size="18" />
          </div>
          <div
            class="action__item"
            v-tooltip="{
              content: 'Cancel',
              html: true,
            }"
            @click="cancelWatch(item)"
          >
            <X :size="18" />
          </div>
        </div>
        <div v-else>-</div>
      </template>
    </CommonTable>

    <CommonModal v-model="isOpenModal" title="Add watch">
      <div v-if="modalItem" class="new-modal">
        <div class="dropdown">
          <CustomTableDropdown
            v-model="companyId"
            :items="companyItems"
            label="Company"
            position="bottom"
            clearable
            fullWidth
            isAppendItem
            dropdownFullWidth
            @search="searchCompany"
          />
        </div>

        <div class="dropdown">
          <CustomTableDropdown
            v-model="modalItem.driver_id"
            :items="driverItems"
            label="Driver"
            fullWidth
            clearable
            position="bottom"
            isAppendItem
            @search="searchDriver"
          />
        </div>

        <CommonDropdown
          v-model="modalItem.target_status"
          :items="targetStatuses"
          label="Target status"
          position="bottom"
          dropdownFullWidth
          fullWidth
        />

        <CommonInput
          v-model="modalItem.expires_in_hours"
          mask="##"
          label="Expire in hours"
        />

        <CommonInput v-model="modalItem.note" label="Note" multiline />
      </div>

      <template #footer>
        <div class="description__footer">
          <CommonButton type="outline" label="Cancel" @clicked="closeModal" />
          <CommonButton type="primary" label="Save" @clicked="saveModal" />
        </div>
      </template>
    </CommonModal>
  </div>
</template>

<style scoped lang="scss">
.v-driver-status {
  display: flex;
  flex-direction: column;
  padding: var(--base-space-24);
  height: calc(100vh - 80px);
  background: var(--color-system-background);

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &__filter {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    &__stats {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }

  .body {
    padding: 0 16px;
  }

  .action {
    display: flex;
    align-items: center;
    gap: 8px;

    &__item {
      width: 32px;
      height: 32px;
      border: 1px solid var(--color-border);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: var(--color-secondary-system-background);
    }
  }
}

.description__footer {
  width: 100%;
  gap: var(--base-space-8);
  display: flex;
  align-items: center;
  justify-content: end;
}

.eld_status {
  display: flex;
  gap: 4px;

  .icon {
    color: var(--color-label);
    background: var(--color-secondary-system-background);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
  }
}

.new-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .dropdown {
    width: 100%;
    border: 1px solid var(--color-border);
    border-radius: 14px;
    background: var(--color-secondary-system-background);

    &:hover {
      border-color: var(--color-primary-disabled);
    }
  }
}
</style>
